import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { socketService, TrainingProgressData } from '../services/trainingSocketService';
import { fetchUserProcesses, convertProcessToTrainingProcess } from '../services/processService';

export interface TrainingProcess {
  id: string;
  name: string;
  status: 'running' | 'completed' | 'failed' | 'paused' | 'cancelled';
  progress: number;
  startTime: string;
  job_id?: string; // RunPod job ID or local process ID
  training_type?: 'runpod' | 'local';
  config?: {
    modelName: string;
    description?: string;
    gpuType?: string;
  };
  // Training data
  trainingProgressData: TrainingProgressData[];
  modelStructure: any | null;
  modelEvaluationInfo: any | null;
  lastTrainingError: string | null;
  epoch: number;
  totalEpochs: number;
  train_loss: number;
  val_loss: number;
}

interface ProcessesContextType {
  processes: TrainingProcess[];
  addProcess: (process: Omit<TrainingProcess, 'trainingProgressData' | 'modelStructure' | 'modelEvaluationInfo' | 'lastTrainingError' | 'epoch' | 'totalEpochs' | 'train_loss' | 'val_loss'>) => string;
  updateProcess: (id: string, updates: Partial<TrainingProcess>) => void;
  removeProcess: (id: string) => void;
  getProcess: (id: string) => TrainingProcess | undefined;
  getProcessByJobId: (job_id: string) => TrainingProcess | undefined;
  clearAllProcesses: () => void;
}

const ProcessesContext = createContext<ProcessesContextType>({
  processes: [],
  addProcess: () => '',
  updateProcess: () => {},
  removeProcess: () => {},
  getProcess: () => undefined,
  getProcessByJobId: () => undefined,
  clearAllProcesses: () => {},
});

export const useProcesses = () => useContext(ProcessesContext);

interface ProcessesProviderProps {
  children: ReactNode;
}

export const ProcessesProvider: React.FC<ProcessesProviderProps> = ({ children }) => {
  const [processes, setProcesses] = useState<TrainingProcess[]>([]);
  const [isLoadingFromSupabase, setIsLoadingFromSupabase] = useState(true);
  
  // Load processes from Supabase on mount
  useEffect(() => {
    let mounted = true;
    
    const loadProcesses = async () => {
      try {
        setIsLoadingFromSupabase(true);
        
        // Load from Supabase (RunPod jobs only)
        const supabaseProcesses = await fetchUserProcesses();
        console.log(`[ProcessesContext] Loaded ${supabaseProcesses.length} processes from Supabase`);
        
        // Convert to TrainingProcess format
        const convertedProcesses = supabaseProcesses.map(convertProcessToTrainingProcess);
        
        // Also load from localStorage (for local jobs and in-memory state)
        let localProcesses: TrainingProcess[] = [];
        try {
          const saved = localStorage.getItem('training_processes');
          if (saved) {
            const parsed = JSON.parse(saved);
            // Filter for local jobs only (RunPod jobs come from Supabase)
            localProcesses = parsed.filter((p: TrainingProcess) => {
              // Must have job_id
              if (!p.job_id || p.job_id === '') {
                return false;
              }
              // Only keep local jobs (RunPod jobs come from Supabase)
              return p.training_type === 'local';
            });
            console.log(`[ProcessesContext] Loaded ${localProcesses.length} local processes from localStorage`);
          }
        } catch (e) {
          console.error('Error loading processes from localStorage:', e);
        }
        
        if (mounted) {
          // Merge: Supabase processes take precedence for RunPod jobs
          // Use a map to deduplicate by job_id
          const processMap = new Map<string, TrainingProcess>();
          
          // First add local processes
          localProcesses.forEach(p => {
            if (p.job_id) {
              processMap.set(p.job_id, p);
            }
          });
          
          // Then add/override with Supabase processes (RunPod jobs)
          convertedProcesses.forEach(p => {
            if (p.job_id) {
              processMap.set(p.job_id, p);
            }
          });
          
          const mergedProcesses = Array.from(processMap.values());
          console.log(`[ProcessesContext] Merged ${mergedProcesses.length} total processes (${convertedProcesses.length} from Supabase, ${localProcesses.length} local)`);
          
          setProcesses(mergedProcesses);
          
          // Auto-reconnect to running jobs
          const runningJobs = mergedProcesses.filter(p => 
            p.status === 'running' && p.training_type === 'runpod' && p.job_id
          );
          
          if (runningJobs.length > 0) {
            console.log(`[ProcessesContext] Auto-reconnecting to ${runningJobs.length} running RunPod jobs`);
            // The websocket service already broadcasts all events to all clients
            // ProcessesContext will filter events by job_id to update correct process
            // No explicit subscription needed - events are broadcast
          }
        }
      } catch (error) {
        console.error('[ProcessesContext] Error loading processes from Supabase:', error);
        // Fallback to localStorage only if Supabase fails
        try {
          const saved = localStorage.getItem('training_processes');
          if (saved && mounted) {
            const parsed = JSON.parse(saved);
            const validProcesses = parsed.filter((p: TrainingProcess) => {
              if (!p.job_id || p.job_id === '') return false;
              if (p.name === 'Untitled Project') return false;
              return true;
            });
            setProcesses(validProcesses);
          }
        } catch (e) {
          console.error('Error loading fallback processes:', e);
        }
      } finally {
        if (mounted) {
          setIsLoadingFromSupabase(false);
        }
      }
    };
    
    loadProcesses();
    
    return () => {
      mounted = false;
    };
  }, []); // Run once on mount

  // Save to localStorage whenever processes change
  // Only save LOCAL jobs to localStorage (RunPod jobs are persisted in Supabase by backend)
  // Strip activations and other large data to prevent quota exceeded errors
  useEffect(() => {
    if (isLoadingFromSupabase) {
      // Don't save while loading from Supabase to avoid overwriting
      return;
    }
    
    try {
      // Filter for local jobs only
      const localProcesses = processes.filter(p => p.training_type === 'local');
      
      // Create a lightweight version without activations for localStorage
      const processesForStorage = localProcesses.map((process) => {
        const { trainingProgressData, ...rest } = process;
        // Strip activations from progress data (they're huge and cause quota errors)
        const lightweightProgress = trainingProgressData?.map(({ activations, weight_changes, ...progressRest }) => progressRest) || [];
        return {
          ...rest,
          trainingProgressData: lightweightProgress,
        };
      });
      localStorage.setItem('training_processes', JSON.stringify(processesForStorage));
    } catch (e) {
      console.error('Error saving processes to localStorage:', e);
      // If still failing, try clearing old data
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        console.warn('[ProcessesContext] localStorage quota exceeded, clearing old processes');
        try {
          // Keep only the most recent local process
          const localProcesses = processes.filter(p => p.training_type === 'local');
          const mostRecent = localProcesses.sort((a, b) => 
            new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
          )[0];
          if (mostRecent) {
            const { trainingProgressData, ...rest } = mostRecent;
            const lightweightProgress = trainingProgressData?.map(({ activations, weight_changes, ...progressRest }) => progressRest) || [];
            localStorage.setItem('training_processes', JSON.stringify([{
              ...rest,
              trainingProgressData: lightweightProgress,
            }]));
          }
        } catch (clearError) {
          console.error('Error clearing localStorage:', clearError);
        }
      }
    }
  }, [processes, isLoadingFromSupabase]);

  const addProcess = useCallback((processData: Omit<TrainingProcess, 'trainingProgressData' | 'modelStructure' | 'modelEvaluationInfo' | 'lastTrainingError' | 'epoch' | 'totalEpochs' | 'train_loss' | 'val_loss'>): string => {
    const id = processData.id || `process_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newProcess: TrainingProcess = {
      ...processData,
      id,
      trainingProgressData: [],
      modelStructure: null,
      modelEvaluationInfo: null,
      lastTrainingError: null,
      epoch: 0,
      totalEpochs: 100, // Default, will be updated
      train_loss: 0,
      val_loss: 0,
    };
    setProcesses((prev) => [...prev, newProcess]);
    return id;
  }, []);

  const updateProcess = useCallback((id: string, updates: Partial<TrainingProcess>) => {
    setProcesses((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...updates } : p))
    );
  }, []);

  const removeProcess = useCallback((id: string) => {
    setProcesses((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const getProcess = useCallback((id: string) => {
    return processes.find((p) => p.id === id);
  }, [processes]);

  const getProcessByJobId = useCallback((job_id: string) => {
    return processes.find((p) => p.job_id === job_id);
  }, [processes]);

  const clearAllProcesses = useCallback(() => {
    console.log('[ProcessesContext] Clearing all processes');
    setProcesses([]);
    try {
      localStorage.removeItem('training_processes');
    } catch (e) {
      console.error('Error clearing processes from localStorage:', e);
    }
  }, []);

  // Cleanup invalid processes on mount (one-time)
  useEffect(() => {
    // Clean up any invalid processes that might have been missed
    setProcesses((prevProcesses) => {
      const cleaned = prevProcesses.filter((p) => {
        // Must have job_id
        if (!p.job_id || p.job_id === '') {
          console.log(`[ProcessesContext] Removing process ${p.id} - no job_id`);
          return false;
        }
        
        // AGGRESSIVE CLEANUP: Remove ALL "Untitled Project" processes that have no training data
        // These are almost certainly invalid/mock data that shouldn't be shown
        if (p.name === 'Untitled Project') {
          const hasTrainingData = p.trainingProgressData && p.trainingProgressData.length > 0;
          
          // Only keep "Untitled Project" if it has actual training data
          if (!hasTrainingData) {
            console.log(`[ProcessesContext] Removing "Untitled Project" process ${p.id} - no training data (status: ${p.status})`);
            return false;
          }
        }
        
        return true;
      });
      
      if (cleaned.length !== prevProcesses.length) {
        console.log(`[ProcessesContext] Cleaned up ${prevProcesses.length - cleaned.length} invalid processes on mount`);
        console.log(`[ProcessesContext] Remaining valid processes: ${cleaned.length}`);
        // Save cleaned list
        try {
          localStorage.setItem('training_processes', JSON.stringify(cleaned));
        } catch (e) {
          console.error('Error saving cleaned processes:', e);
        }
      }
      
      return cleaned;
    });
  }, []); // Run once on mount

  // Listen to WebSocket events and update processes
  useEffect(() => {
    const trainingStartedHandler = (data: any) => {
      console.log('[ProcessesContext] trainingStarted event received:', data);
      const job_id = data.job_id;
      const modelName = data.model_name || data.modelName || 'Untitled Project';
      const training_type = data.training_type || 'local';
      
      // CRITICAL: Only create process if we have a job_id from the backend
      // If no job_id is provided, wait - don't create a process yet
      if (!job_id) {
        console.warn('[ProcessesContext] trainingStarted event missing job_id, waiting for proper job_id...');
        // Generate a temporary ID but don't create process yet - wait for actual job_id
        return;
      }
      
      console.log('[ProcessesContext] Creating/updating process with job_id:', job_id, 'modelName:', modelName, 'type:', training_type);
      
      // Note: For RunPod jobs, the backend already saved to Supabase
      // We just need to update in-memory state here
      // For local jobs, we'll save to localStorage via the useEffect
      
      // Use functional update to get latest processes state
      setProcesses((prevProcesses) => {
        // Check if process already exists
        const existingProcess = prevProcesses.find((p) => p.job_id === job_id);
        
        if (!existingProcess) {
          // Create new process - ONLY when we have a valid job_id from backend
          const newProcess: TrainingProcess = {
            id: `process_${job_id}`,
            name: modelName,
            status: 'running',
            progress: 0,
            startTime: new Date().toLocaleString(),
            job_id: job_id,
            training_type,
            config: {
              modelName,
              description: data.description,
              gpuType: data.gpuType,
            },
            trainingProgressData: [],
            modelStructure: null,
            modelEvaluationInfo: null,
            lastTrainingError: null,
            epoch: 0,
            totalEpochs: 100,
            train_loss: 0,
            val_loss: 0,
          };
          console.log('[ProcessesContext] Adding new process:', newProcess.id, 'name:', newProcess.name, 'type:', training_type);
          return [...prevProcesses, newProcess];
        } else {
          // Update existing process
          console.log('[ProcessesContext] Updating existing process:', existingProcess.id);
          return prevProcesses.map((p) => 
            p.id === existingProcess.id 
              ? { ...p, status: 'running', progress: 0, name: modelName }
              : p
          );
        }
      });
    };

    const trainingProgressHandler = (data: TrainingProgressData & { job_id?: string }) => {
      const job_id = data.job_id;
      if (!job_id) {
        console.warn('[ProcessesContext] trainingProgress event missing job_id:', data);
        return;
      }

      console.log('[ProcessesContext] trainingProgress event for job_id:', job_id, 'epoch:', data.epoch);
      
      // Use functional update to get latest processes state
      setProcesses((prevProcesses) => {
        const process = prevProcesses.find((p) => p.job_id === job_id);
        if (!process) {
          console.warn('[ProcessesContext] No process found for job_id:', job_id);
          return prevProcesses;
        }

        // Update progress data - deduplicate by epoch (replace if same epoch exists)
        const currentProgress = process.trainingProgressData || [];
        const epoch = data.epoch || 0;
        
        // Check if we already have data for this epoch
        const existingIndex = currentProgress.findIndex((p) => p.epoch === epoch);
        let newProgress: TrainingProgressData[];
        
        if (existingIndex >= 0) {
          // Replace existing entry for this epoch (deduplicate)
          newProgress = [...currentProgress];
          newProgress[existingIndex] = data;
        } else {
          // Add new entry
          newProgress = [...currentProgress, data];
          // Limit to last 1000 progress points to prevent localStorage overflow
          if (newProgress.length > 1000) {
            newProgress = newProgress.slice(-1000);
          }
        }

        // Calculate progress percentage
        const totalEpochs = process.totalEpochs || 100;
        const progress = Math.min((epoch / totalEpochs) * 100, 100);

        return prevProcesses.map((p) =>
          p.id === process.id
            ? {
                ...p,
                trainingProgressData: newProgress,
                epoch,
                progress,
                train_loss: data.train_loss || 0,
                val_loss: data.val_loss || 0,
              }
            : p
        );
      });
    };

    const trainingCompletedHandler = (data: any) => {
      const job_id = data.job_id;
      if (!job_id) return;

      setProcesses((prevProcesses) => {
        const process = prevProcesses.find((p) => p.job_id === job_id);
        if (!process) return prevProcesses;

        return prevProcesses.map((p) =>
          p.id === process.id
            ? {
                ...p,
                status: 'completed',
                progress: 100,
                epoch: data.epochs_trained || p.epoch,
              }
            : p
        );
      });
    };

    const trainingErrorHandler = (data: { message?: string; job_id?: string }) => {
      const job_id = data.job_id;
      if (!job_id) return;

      setProcesses((prevProcesses) => {
        const process = prevProcesses.find((p) => p.job_id === job_id);
        if (!process) return prevProcesses;

        return prevProcesses.map((p) =>
          p.id === process.id
            ? {
                ...p,
                status: 'failed',
                lastTrainingError: data.message || 'Unknown error',
              }
            : p
        );
      });
    };

    const trainingCancelledHandler = (data: any) => {
      const job_id = data.job_id;
      if (!job_id) return;

      setProcesses((prevProcesses) => {
        const process = prevProcesses.find((p) => p.job_id === job_id);
        if (!process) return prevProcesses;

        return prevProcesses.map((p) =>
          p.id === process.id ? { ...p, status: 'cancelled' } : p
        );
      });
    };

    const modelStructureHandler = (data: any & { job_id?: string }) => {
      const job_id = data.job_id;
      if (!job_id) return;

      setProcesses((prevProcesses) => {
        const process = prevProcesses.find((p) => p.job_id === job_id);
        if (!process) return prevProcesses;

        // Remove job_id from structure data before storing
        const { job_id: _, ...structureData } = data;
        return prevProcesses.map((p) =>
          p.id === process.id ? { ...p, modelStructure: structureData } : p
        );
      });
    };

    const modelEvaluationInfoHandler = (data: any & { job_id?: string }) => {
      const job_id = data.job_id;
      if (!job_id) return;

      setProcesses((prevProcesses) => {
        const process = prevProcesses.find((p) => p.job_id === job_id);
        if (!process) return prevProcesses;

        // Remove job_id from evaluation data before storing
        const { job_id: _, ...evaluationData } = data;
        return prevProcesses.map((p) =>
          p.id === process.id ? { ...p, modelEvaluationInfo: evaluationData } : p
        );
      });
    };

    // Register event listeners
    socketService.on('trainingStarted', trainingStartedHandler);
    socketService.on('trainingProgress', trainingProgressHandler);
    socketService.on('trainingCompleted', trainingCompletedHandler);
    socketService.on('trainingError', trainingErrorHandler);
    socketService.on('trainingCancelled', trainingCancelledHandler);
    socketService.on('modelStructure', modelStructureHandler);
    socketService.on('modelEvaluationInfo', modelEvaluationInfoHandler);

    return () => {
      socketService.off('trainingStarted', trainingStartedHandler);
      socketService.off('trainingProgress', trainingProgressHandler);
      socketService.off('trainingCompleted', trainingCompletedHandler);
      socketService.off('trainingError', trainingErrorHandler);
      socketService.off('trainingCancelled', trainingCancelledHandler);
      socketService.off('modelStructure', modelStructureHandler);
      socketService.off('modelEvaluationInfo', modelEvaluationInfoHandler);
    };
  }, []); // Empty deps - handlers use functional updates to access latest state

  const value: ProcessesContextType = {
    processes,
    addProcess,
    updateProcess,
    removeProcess,
    getProcess,
    getProcessByJobId,
    clearAllProcesses,
  };

  return <ProcessesContext.Provider value={value}>{children}</ProcessesContext.Provider>;
};

export default ProcessesContext;

